import React, { useRef, useEffect, useState, useCallback } from 'react';
import '../style/hero.css';
import { 
  FaChevronLeft, 
  FaChevronRight, 
  FaPlay, 
  FaPause, 
  FaStepBackward, 
  FaStepForward, 
  FaTimes,
  FaVolumeMute,
  FaVolumeUp
} from 'react-icons/fa';

// Import videos
import reel1 from '../../assets/reels/cgi/Inshot 20250325 173420235.mp4';
import reel2 from '../../assets/reels/cgi/Od.mp4';
import reel3 from '../../assets/reels/events/Metro.mp4';
import reel4 from '../../assets/reels/events/Pepsi.mp4';
import reel5 from '../../assets/reels/fashion/Fashion Shoes.mp4';
import reel6 from '../../assets/reels/fashion/Funkari Post.mp4';
import reel7 from '../../assets/reels/food/Fluree.mp4';
import reel8 from '../../assets/reels/food/Contra Coffe.mp4';
import reel9 from '../../assets/reels/real state/Inshot 20250730 190157690.mp4';
import reel10 from '../../assets/reels/real state/Fd Tiles 3.mp4';
import reel11 from '../../assets/reels/speed ramping/Bts 2.mp4';
import reel12 from '../../assets/reels/speed ramping/Doctor 1.mp4';
import reel13 from '../../assets/reels/speed ramping/Funnation Carnival Final 4.mp4';
import reel14 from '../../assets/reels/speed ramping/Ideas 2.mp4';
import reel15 from '../../assets/reels/top 10/Jumbo Ae.mp4';
import reel16 from '../../assets/reels/top 10/Faizaia Society.mp4';
import reel17 from '../../assets/reels/top 10/Geek Out.mp4';
import reel18 from '../../assets/reels/top 10/Kia.mp4';
import reel19 from '../../assets/reels/top 10/Oddd.mp4';
import reel20 from '../../assets/reels/top 10/Brim Outlet.mp4';

const Hero = () => {
  const scrollRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);
  const [selectedReel, setSelectedReel] = useState(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(true); // Start muted by default
  const [reelWidth, setReelWidth] = useState(220);
  const popupVideoRef = useRef(null);
  const modalRef = useRef(null);
  const autoScrollRef = useRef(null);
  const isScrolling = useRef(false);

  const reels = [
    reel1, reel2, reel3, reel4, reel5,
    reel6, reel7, reel8, reel9, reel10,
    reel11, reel12, reel13, reel14, reel15,
    reel16, reel17, reel18, reel19, reel20
  ];

  // Update reel width based on screen size
  useEffect(() => {
    const updateReelWidth = () => {
      if (window.innerWidth >= 1024) {
        setReelWidth(220);
      } else if (window.innerWidth >= 768) {
        setReelWidth(200);
      } else if (window.innerWidth >= 480) {
        setReelWidth(160);
      } else {
        setReelWidth(140);
      }
    };

    updateReelWidth();
    window.addEventListener('resize', updateReelWidth);
    return () => window.removeEventListener('resize', updateReelWidth);
  }, []);

  // Fix video smoothness - preload videos
  useEffect(() => {
    if (selectedReel !== null && popupVideoRef.current) {
      const video = popupVideoRef.current;
      
      // Force reload video for smooth playback
      video.load();
      
      // Set video to play smoothly
      video.playbackRate = 1.0;
      video.defaultPlaybackRate = 1.0;
      
      // Set mute state
      video.muted = isMuted;
      
      // Handle video buffering
      video.addEventListener('waiting', () => {
        console.log('Video is buffering...');
      });
      
      video.addEventListener('canplay', () => {
        console.log('Video can play smoothly');
        if (isPlaying) {
          video.play().catch(error => {
            console.log("Auto-play prevented:", error);
          });
        }
      });
      
      // Ensure video starts playing
      if (isPlaying) {
        const playPromise = video.play();
        if (playPromise !== undefined) {
          playPromise.catch(error => {
            console.log("Auto-play prevented:", error);
          });
        }
      }
    }
  }, [selectedReel, isPlaying, isMuted]);

  // FIXED NAVIGATION BUTTONS - NEW SIMPLE LOGIC
  const scrollLeft = () => {
    if (scrollRef.current && !isScrolling.current) {
      isScrolling.current = true;
      
      // Get visible width and scroll by 80% of it
      const containerWidth = scrollRef.current.clientWidth;
      const scrollAmount = Math.floor(containerWidth * 0.8);
      
      scrollRef.current.scrollBy({
        left: -scrollAmount,
        behavior: 'smooth'
      });
      
      // Reset scrolling flag after animation
      setTimeout(() => {
        isScrolling.current = false;
      }, 500);
    }
  };

  const scrollRight = () => {
    if (scrollRef.current && !isScrolling.current) {
      isScrolling.current = true;
      
      // Get visible width and scroll by 80% of it
      const containerWidth = scrollRef.current.clientWidth;
      const scrollAmount = Math.floor(containerWidth * 0.8);
      
      scrollRef.current.scrollBy({
        left: scrollAmount,
        behavior: 'smooth'
      });
      
      // Check if we need to loop to start
      setTimeout(() => {
        const maxScroll = scrollRef.current.scrollWidth - scrollRef.current.clientWidth;
        if (scrollRef.current.scrollLeft >= maxScroll - 100) {
          scrollRef.current.scrollTo({
            left: 0,
            behavior: 'smooth'
          });
        }
        isScrolling.current = false;
      }, 500);
    }
  };

  // Auto-scroll animation
  useEffect(() => {
    if (isHovered || selectedReel !== null) {
      if (autoScrollRef.current) {
        clearInterval(autoScrollRef.current);
        autoScrollRef.current = null;
      }
      return;
    }

    autoScrollRef.current = setInterval(() => {
      if (scrollRef.current && !isScrolling.current) {
        const maxScroll = scrollRef.current.scrollWidth - scrollRef.current.clientWidth;
        const currentScroll = scrollRef.current.scrollLeft;
        
        if (currentScroll >= maxScroll - 10) {
          // Seamless loop back to start
          scrollRef.current.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          // Smooth auto-scroll
          scrollRef.current.scrollBy({ left: 1, behavior: 'auto' });
        }
      }
    }, 20);

    return () => {
      if (autoScrollRef.current) {
        clearInterval(autoScrollRef.current);
      }
    };
  }, [isHovered, selectedReel]);

  // Handle reel click for popup
  const handleReelClick = (index) => {
    setSelectedReel(index);
    setIsPlaying(true);
    setIsMuted(true); // Start muted when opening new reel
  };

  // Close popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectedReel !== null && 
          modalRef.current && 
          !modalRef.current.contains(event.target)) {
        setSelectedReel(null);
      }
    };

    if (selectedReel !== null) {
      document.addEventListener('mousedown', handleClickOutside);
      // Pause background auto-scroll
      if (autoScrollRef.current) {
        clearInterval(autoScrollRef.current);
      }
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [selectedReel]);

  // Handle keyboard navigation for popup
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (selectedReel !== null) {
        switch(e.key) {
          case 'Escape':
            setSelectedReel(null);
            break;
          case 'ArrowLeft':
            handlePrevReel();
            break;
          case 'ArrowRight':
            handleNextReel();
            break;
          case ' ':
            e.preventDefault();
            togglePlayPause();
            break;
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedReel, isPlaying]);

  // Video control functions
  const togglePlayPause = () => {
    if (popupVideoRef.current) {
      if (isPlaying) {
        popupVideoRef.current.pause();
        setIsPlaying(false);
      } else {
        const playPromise = popupVideoRef.current.play();
        if (playPromise !== undefined) {
          playPromise
            .then(() => setIsPlaying(true))
            .catch(error => {
              console.log("Play prevented:", error);
            });
        }
      }
    }
  };

  const toggleMute = () => {
    if (popupVideoRef.current) {
      const newMutedState = !isMuted;
      popupVideoRef.current.muted = newMutedState;
      setIsMuted(newMutedState);
    }
  };

  const handlePrevReel = () => {
    setSelectedReel(prev => {
      const newIndex = prev === 0 ? reels.length - 1 : prev - 1;
      setIsPlaying(true);
      setIsMuted(true); // Reset mute for new reel
      return newIndex;
    });
  };

  const handleNextReel = () => {
    setSelectedReel(prev => {
      const newIndex = prev === reels.length - 1 ? 0 : prev + 1;
      setIsPlaying(true);
      setIsMuted(true); // Reset mute for new reel
      return newIndex;
    });
  };

  // Handle hover events
  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  return (
    <div className="hero">
      {/* Animated Background */}
      <div className="hero-pattern">
        {[...Array(8)].map((_, i) => (
          <div key={i} className={`fx-laser-ray fx-ray-${i + 1}`}></div>
        ))}
        <div className="fx-grid-pattern"></div>
        <div className="fx-pulse-effect"></div>
      </div>

      {/* Hero Text */}
      <div className="hero-content">
        <h1 className="hero-heading">
          YOUR VISION. OUR VISUALS.
          <br />
          ONE POWERFUL STORY.
        </h1>
        <div className="hero-tagline">
          BE SEEN . BE REMEMBERED . CREATE WITH US.
        </div>
      </div>

      {/* Reels with Buttons */}
      <div className="reels-container">
        <div 
          className={`reels-scroll ${isHovered ? 'paused' : ''}`} 
          ref={scrollRef}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {reels.map((reel, index) => (
            <div 
              key={index} 
              className="reel-item"
              onClick={() => handleReelClick(index)}
            >
              <video
                preload="auto"
                autoPlay
                muted
                loop
                playsInline
                className="reel-video"
              >
                <source src={reel} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
          ))}
          {reels.map((reel, index) => (
            <div 
              key={`dup-${index}`} 
              className="reel-item"
              onClick={() => handleReelClick(index)}
            >
              <video
                preload="auto"
                autoPlay
                muted
                loop
                playsInline
                className="reel-video"
              >
                <source src={reel} type="video/mp4" />
              </video>
            </div>
          ))}
        </div>

        {/* Navigation Buttons */}
        {/* <button className="nav-btn prev-btn" onClick={scrollLeft}>
          <FaChevronLeft />
        </button>
        <button className="nav-btn next-btn" onClick={scrollRight}>
          <FaChevronRight />
        </button> */}
      </div>

      {/* Reel Popup within Hero Section */}
      {selectedReel !== null && (
        <div className="reel-popup-overlay">
          <div className="reel-popup" ref={modalRef}>
            <button 
              className="popup-close-btn"
              onClick={() => setSelectedReel(null)}
            >
              <FaTimes />
            </button>
            
            <div className="popup-video-container">
              <video
                ref={popupVideoRef}
                autoPlay={isPlaying}
                muted={isMuted}
                loop
                playsInline
                controls={false}
                className="popup-video"
                preload="auto"
                webkit-playsinline="true"
                x5-playsinline="true"
              >
                <source src={reels[selectedReel]} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>

            {/* Video Controls */}
            <div className="popup-controls">
              <button 
                className="popup-control-btn prev-control"
                onClick={handlePrevReel}
              >
                <FaStepBackward />
              </button>
              
              <button 
                className="popup-control-btn play-pause-control"
                onClick={togglePlayPause}
              >
                {isPlaying ? <FaPause /> : <FaPlay />}
              </button>

              {/* Mute/Unmute Button */}
              <button 
                className="popup-control-btn mute-control"
                onClick={toggleMute}
                aria-label={isMuted ? 'Unmute' : 'Mute'}
              >
                {isMuted ? <FaVolumeMute /> : <FaVolumeUp />}
              </button>
              
              <button 
                className="popup-control-btn next-control"
                onClick={handleNextReel}
              >
                <FaStepForward />
              </button>
            </div>

            <div className="popup-counter">
              {selectedReel + 1} / {reels.length}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Hero;